import React, { useContext } from 'react';
import { observer } from 'mobx-react-lite';
import { TenantEmployment } from '../tenantProfile/tenantEmployment';
import { GridContent, GridMainContent, GridSideContent } from '../uiComponents/UIComponents';
import { Button, Card, CardActions, CardContent, Typography } from '@material-ui/core';
import { IncomeCard } from '../income/incomeCard';
import { Grid, Link } from '@mui/material';
import { paths } from '../../routes';
import { IncomeType, IOccupation, IProofOfOccupationDocument, ITenantProfile } from 'realhaus-sdk';
import { RootStoreContext } from '../../global/storeContext';

interface FinanceInfoProps {
    profile: ITenantProfile;
    updateProfile: (prop: keyof ITenantProfile) => (value: IOccupation[]) => void;
}

export const FinanceInfoComponent: React.FC<FinanceInfoProps> = observer(
    ({ profile, updateProfile }) => {
        return (
            <>
                <GridContent spacing={5}>
                    <GridMainContent>
                        <Typography variant='h5' gutterBottom>
                            What is your financial situation?
                        </Typography>
                    </GridMainContent>
                    <GridMainContent withSidebar>
                        <OccupationComponent
                            employments={profile.occupation}
                            update={updateProfile('occupation')}
                        />
                    </GridMainContent>
                    <GridSideContent>
                        <Typography variant='body1' align='justify'>
                            In this section, please provide accurate information about your current
                            employment. This helps the landlord better understand your financial
                            situation and determine your ability to meet the rental requirements. If
                            you have multiple sources of income that you'd like to include, you can
                            update your employment details through your{' '}
                            <Link color='primary' href={paths.tenant.profile}>
                                <strong>profile page</strong>
                            </Link>
                        </Typography>
                    </GridSideContent>
                </GridContent>
            </>
        );
    },
);

/**
 * Props for OccupationComponent.
 * @property {IOccupation[]} employments - List of occupation objects to display and edit.
 * @property {(occupation: IOccupation[]) => void} update - Callback to update the occupation list.
 */
export const OccupationComponent: React.FC<{
    employments: IOccupation[];
    update: (occupation: IOccupation[]) => void;
}> = observer(({ employments, update }) => {
    const [selectedOccupationIndex, setSelectedOccupationIndex] = React.useState<number>(
        Number.NaN,
    );
    const [selectedOccupation, setSelectedOccupation] = React.useState<IOccupation>();
    const [occupations, setOccupations] = React.useState<IOccupation[]>(employments);
    const { tenantStore } = useContext(RootStoreContext);

    React.useEffect(() => {
        setOccupations(employments);
    }, [employments]);

    const newOccupationIndex = -1;

    const unSelectOccupation = () => {
        // adjust selected occupation state
        setSelectedOccupationIndex(Number.NaN);
        setSelectedOccupation(undefined);
    };

    const onEditOccupation = (index: number) => {
        if (index < 0 || occupations.length === 0 || occupations.length <= index) {
            return;
        }

        setSelectedOccupationIndex(index);
        setSelectedOccupation({ ...occupations[index] });
    };

    const defaultOccupation: IOccupation = {
        title: '',
        status: '',
        businessType: '',
        employer: '',
        startDate: 0,
        income: 0,
        incomeType: IncomeType.MONTHLY,
        manager: '',
    };

    /**
     * Handles the creation of a new occupation entry.
     * Resets the selected occupation index and initializes a new occupation object
     * with default values.
     */
    const onCreateOccupation = () => {
        setSelectedOccupationIndex(newOccupationIndex);
        setSelectedOccupation({ ...defaultOccupation });
    };

    /**
     * Updates the occupation information in the tenant profile.
     * @param dataToUpdate - The occupation data to update or add.
     */
    const onUpdateOccupation = (dataToUpdate: IOccupation) => {
        if (dataToUpdate.proofOfOccupation?.fileUrl) setSelectedOccupation({ ...dataToUpdate });

        const toUpdate = [...occupations];

        if (selectedOccupationIndex === newOccupationIndex) {
            toUpdate.push(dataToUpdate);
        } else {
            toUpdate[selectedOccupationIndex] = dataToUpdate;
        }

        //ToDo: save to DB
        update(toUpdate);
        setOccupations([...toUpdate]);
    };

    const onDeleteOccupation = (index: number) => {
        if (
            index < 0 ||
            occupations.length === 0 ||
            occupations.length <= index ||
            selectedOccupationIndex !== index
        ) {
            return;
        }

        const toUpdate = occupations.filter((_, i) => i !== index);

        //ToDo: save to DB
        update(toUpdate);
        setOccupations(toUpdate);
    };

    const deleteUnwrittenProofOfOccupationDocument = async () => {
        // selected occupation
        // occupation in DB
        try {
            // delete old document
            console.log(selectedOccupation?.proofOfOccupation?.fileUrl);
            if (selectedOccupation?.proofOfOccupation?.fileUrl) {
                if (selectedOccupationIndex >= 0) {
                    const currentPOO =
                        occupations[selectedOccupationIndex]?.proofOfOccupation?.fileUrl;
                    if (currentPOO !== selectedOccupation.proofOfOccupation.fileUrl) {
                        // delete uploaded but not saved document
                        await tenantStore.deleteDocument(
                            selectedOccupation.proofOfOccupation.fileUrl,
                        );
                    }
                } else {
                    // delete uploaded but not saved document
                    await tenantStore.deleteDocument(selectedOccupation.proofOfOccupation.fileUrl);
                }
            }
        } catch (err) {
            console.error('Error deleting old proof of occupation document:', err);
        }
    };

    const occupationForm = (occupation: IOccupation) => {
        if (!selectedOccupation) return null;
        return (
            <Grid item xs={12}>
                <Card variant='outlined'>
                    <CardContent>
                        <TenantEmployment
                            employment={selectedOccupation}
                            updateEmployment={(prop: keyof IOccupation) => async (value) => {
                                let v = value as any;
                                if (
                                    prop === 'proofOfOccupation' &&
                                    (value as any).file &&
                                    (value as any).file instanceof File
                                ) {
                                    try {
                                        // upload selected file
                                        const doc =
                                            await tenantStore.uploadProofOfOccupationDocument(
                                                (value as any).file,
                                                (value as IProofOfOccupationDocument).type,
                                            );

                                        if (doc) {
                                            await deleteUnwrittenProofOfOccupationDocument();

                                            // update proofOfOccupation url
                                            v = {
                                                type: (value as IProofOfOccupationDocument).type,
                                                fileUrl: doc.fileUrl,
                                            } as IProofOfOccupationDocument;
                                        } else {
                                            // unable to upload file
                                            return;
                                        }
                                    } catch (err) {
                                        console.error(
                                            'Error uploading proof of occupation document:',
                                            err,
                                        );
                                        return;
                                    }
                                }

                                const updated = {
                                    ...selectedOccupation,
                                    [prop]: v,
                                };
                                setSelectedOccupation({ ...updated });
                            }}
                        />
                    </CardContent>
                    <CardActions>
                        <Grid
                            container
                            xs={12}
                            paddingTop={1}
                            justifyContent='flex-end'
                            direction='row'
                        >
                            <Button
                                variant='outlined'
                                color='primary'
                                size='small'
                                disabled={
                                    !selectedOccupation ||
                                    JSON.stringify(selectedOccupation) ===
                                        JSON.stringify(occupation)
                                }
                                onClick={() => {
                                    if (selectedOccupation) {
                                        onUpdateOccupation(selectedOccupation);
                                        unSelectOccupation();
                                    }
                                }}
                            >
                                Save
                            </Button>
                            <Button
                                size='small'
                                onClick={async () => {
                                    await deleteUnwrittenProofOfOccupationDocument();
                                    unSelectOccupation();
                                }}
                            >
                                Cancel
                            </Button>
                        </Grid>
                    </CardActions>
                </Card>
            </Grid>
        );
    };

    return (
        <>
            <Grid container spacing={1}>
                <Grid container item direction='row' justifyContent='end' xs={12}>
                    <Button
                        variant='outlined'
                        color='primary'
                        size='small'
                        onClick={onCreateOccupation}
                    >
                        Add occupation
                    </Button>
                </Grid>

                {selectedOccupationIndex === newOccupationIndex &&
                    !!selectedOccupation &&
                    occupationForm({ ...defaultOccupation })}

                {occupations.map((occupation, index) => (
                    <Grid item xs={12} key={index}>
                        {selectedOccupationIndex !== index && (
                            <IncomeCard
                                occupation={occupation}
                                onEdit={() => onEditOccupation(index)}
                                onDelete={() => onDeleteOccupation(index)}
                            />
                        )}
                        {selectedOccupationIndex === index &&
                            !!selectedOccupation &&
                            occupationForm(occupation)}
                    </Grid>
                ))}
            </Grid>
        </>
    );
});
